import { create } from 'zustand'
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore'
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage'
import { db, storage, functions } from '../lib/firebase'
import { httpsCallable } from 'firebase/functions'
import { Project, Scene, Image, Video, Comment, Reply, ProjectSettings, StoryContent, ModuleType, VideoMetadata } from '../types'
import * as fal from '@fal-ai/serverless-client'
import { generateThumbnail, getThumbnailFilename } from '../utils/imageUtils'

// Initialize fal if API key is present
const falApiKey = import.meta.env.VITE_FAL_API_KEY
if (falApiKey && falApiKey !== 'your_fal_api_key_here') {
  fal.config({
    credentials: falApiKey
  })
}

export type GenerationMode = 'seedream' | 'seedream-edit' | 'nano-banana' | 'nano-banana-edit' | 'nano-banana-pro' | 'nano-banana-pro-edit' | 'flux-pro' | 'character'

export interface GenerationRequest {
  id: string
  prompt: string
  mode: GenerationMode
  sceneId: string
  projectId: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  progress?: number
  stage?: string
  error?: string
  settings: {
    resolution?: string
    aspectRatio?: string
    numImages: number
    imageUrls?: string[] // Uploaded reference images (URLs from fal.storage)
    seed?: number
    // Flux specific
    imageSize?: string // Enum for Flux: landscape_4_3, etc.
    safetyTolerance?: string // "1" to "5"
    enableSafetyChecker?: boolean
    outputFormat?: string // jpeg, png
    // Character LoRA specific
    characterId?: string
    characterName?: string
    loraWeightsUrl?: string
    loraScale?: number // 0.0 to 1.0, default 1.0
    // HD mode specific (nano-banana-pro)
    hdResolution?: string // '1k', '2k', '4k'
    hdWidth?: number
    hdHeight?: number
  }
  // Store the temporary image IDs for UI feedback
  tempImageIds?: string[]
}

// Image cache entry with timestamp
interface ImageCacheEntry {
  images: Image[]
  fetchedAt: number
}

// Video cache entry with timestamp
interface VideoCacheEntry {
  videos: Video[]
  fetchedAt: number
}

// Cache expiration time (5 minutes)
const IMAGE_CACHE_TTL = 5 * 60 * 1000
const VIDEO_CACHE_TTL = 5 * 60 * 1000

interface ProjectState {
  projects: Project[]
  currentProject: Project | null
  scenes: Scene[]
  images: Image[]
  videos: Video[]
  // Current module being viewed (Story, Images, Video)
  currentModule: ModuleType
  // Image cache per scene - prevents refetching when switching scenes
  imageCache: Map<string, ImageCacheEntry>
  // Video cache per scene
  videoCache: Map<string, VideoCacheEntry>
  currentSceneId: string | null
  loading: boolean
  error: string | null

  // Generation Queue
  generationQueue: GenerationRequest[]
  isProcessingQueue: boolean
  addToGenerationQueue: (request: Omit<GenerationRequest, 'status' | 'id'>) => Promise<void>
  removeFromGenerationQueue: (id: string) => void
  clearCompletedGenerations: () => void
  clearQueue: () => void

  // Project actions
  fetchProjects: (userId: string) => Promise<void>
  fetchProjectById: (projectId: string) => Promise<Project | null>
  createProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>
  updateProject: (id: string, updates: Partial<Project>) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  setCurrentProject: (project: Project | null) => void

  // Scene actions
  fetchScenes: (projectId: string) => Promise<void>
  createScene: (scene: Omit<Scene, 'id' | 'createdAt' | 'updatedAt'>, initialImageId?: string) => Promise<string>
  updateScene: (id: string, updates: Partial<Scene>) => Promise<void>
  deleteScene: (id: string) => Promise<void>
  reorderScenes: (projectId: string, sceneIds: string[]) => Promise<void>

  // Image actions
  fetchImages: (sceneId: string, forceRefresh?: boolean) => Promise<void>
  uploadImage: (file: File, sceneId: string, projectId: string) => Promise<string>
  generateImage: (imageUrl: string, prompt: string, sceneId: string, projectId: string) => Promise<string>
  updateImage: (id: string, updates: Partial<Image>) => Promise<void>
  moveImageToScene: (imageId: string, targetSceneId: string) => Promise<void>
  deleteImage: (id: string) => Promise<void>
  setMainImage: (imageId: string, sceneId: string) => Promise<void>
  toggleFavorite: (imageId: string) => Promise<void>
  invalidateImageCache: (sceneId: string) => void
  clearImageCache: () => void

  // Comment actions
  addComment: (imageId: string, content: string, userId: string, userEmail: string) => Promise<void>
  addReply: (commentId: string, content: string, userId: string, userEmail: string) => Promise<void>
  deleteComment: (commentId: string, imageId: string) => Promise<void>

  // Module actions
  setCurrentModule: (module: ModuleType) => void

  // Video actions
  fetchVideos: (sceneId: string, forceRefresh?: boolean) => Promise<void>
  uploadVideo: (file: File, sceneId: string, projectId: string) => Promise<string>
  updateVideo: (id: string, updates: Partial<Video>) => Promise<void>
  deleteVideo: (id: string) => Promise<void>
  setMainVideo: (videoId: string, sceneId: string) => Promise<void>
  toggleVideoFavorite: (videoId: string) => Promise<void>
  invalidateVideoCache: (sceneId: string) => void
  clearVideoCache: () => void
  addVideoComment: (videoId: string, content: string, userId: string, userEmail: string) => Promise<void>

  // Story content actions
  updateStoryContent: (sceneId: string, storyContent: Partial<StoryContent>) => Promise<void>

  // Settings actions
  updateProjectSettings: (projectId: string, settings: ProjectSettings) => Promise<void>

  // Thumbnail actions
  regenerateThumbnails: (projectId: string, sceneId?: string) => Promise<{ success: boolean; processed?: { success: number; failed: number; skipped: number }; error?: string }>

  // Utility
  clearError: () => void
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  scenes: [],
  images: [],
  videos: [],
  currentModule: 'images' as ModuleType, // Default to Images module
  imageCache: new Map<string, ImageCacheEntry>(),
  videoCache: new Map<string, VideoCacheEntry>(),
  currentSceneId: null,
  loading: false,
  error: null,
  
  generationQueue: [],
  isProcessingQueue: false,

  addToGenerationQueue: async (requestData) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
    const newRequest: GenerationRequest = {
      ...requestData,
      id,
      status: 'pending',
      stage: 'Queued',
      progress: 0
    }

    set(state => ({
      generationQueue: [...state.generationQueue, newRequest]
    }))

    // Trigger processing if not already running
    const { isProcessingQueue } = get()
    if (!isProcessingQueue) {
      processQueue(get, set)
    }
  },

  removeFromGenerationQueue: (id) => {
    set(state => ({
      generationQueue: state.generationQueue.filter(req => req.id !== id)
    }))
  },

  clearCompletedGenerations: () => {
    set(state => ({
      generationQueue: state.generationQueue.filter(req => req.status !== 'completed')
    }))
  },

  clearQueue: () => {
    set(state => ({
      // Keep only currently processing item (if any), clear all pending/completed/failed
      generationQueue: state.generationQueue.filter(req => req.status === 'processing')
    }))
  },

  fetchProjects: async (userId: string) => {
    try {
      set({ loading: true, error: null })
      // Temporary: Remove ordering until Firestore index is ready
      const q = query(
        collection(db, 'projects'),
        where('ownerId', '==', userId)
        // orderBy('updatedAt', 'desc') // Uncomment when index is ready
      )
      const snapshot = await getDocs(q)
      const projects = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          ...data,
          // Set defaults for fields that might not exist in older projects
          isPublic: data.isPublic ?? false,
          allowComments: data.allowComments ?? true,
          createdAt: data.createdAt?.toDate().toISOString(),
          updatedAt: data.updatedAt?.toDate().toISOString(),
        }
      }) as Project[]
      
      set({ projects })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch projects' })
    } finally {
      set({ loading: false })
    }
  },

  fetchProjectById: async (projectId: string) => {
    try {
      set({ loading: true, error: null })
      console.log('fetchProjectById called for:', projectId)
      const docRef = doc(db, 'projects', projectId)
      const docSnap = await getDoc(docRef)
      
      if (docSnap.exists()) {
        const data = docSnap.data()
        console.log('Project data from Firebase:', { id: docSnap.id, isPublic: data.isPublic, allowComments: data.allowComments })
        
        const project = {
          id: docSnap.id,
          ...data,
          // Set defaults for fields that might not exist in older projects
          isPublic: data.isPublic ?? false,
          allowComments: data.allowComments ?? true,
          createdAt: data.createdAt?.toDate().toISOString(),
          updatedAt: data.updatedAt?.toDate().toISOString(),
        } as Project
        
        // Add to projects array if not already there, or update existing
        set(state => ({
          projects: state.projects.some(p => p.id === projectId)
            ? state.projects.map(p => p.id === projectId ? project : p)
            : [...state.projects, project],
          currentProject: project
        }))
        
        console.log('Project updated in store:', { isPublic: project.isPublic, allowComments: project.allowComments })
        
        return project
      }
      
      console.log('Project not found in Firebase')
      return null
    } catch (error) {
      console.error('fetchProjectById error:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to fetch project' })
      return null
    } finally {
      set({ loading: false })
    }
  },

  createProject: async (projectData) => {
    try {
      set({ loading: true, error: null })
      const docRef = await addDoc(collection(db, 'projects'), {
        ...projectData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        totalScenes: 0,
        totalImages: 0,
      })
      
      // Refresh projects
      const { fetchProjects } = get()
      await fetchProjects(projectData.ownerId)
      
      return docRef.id
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create project' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  updateProject: async (id, updates) => {
    try {
      set({ loading: true, error: null })
      console.log('updateProject called with:', { id, updates })
      
      await updateDoc(doc(db, 'projects', id), {
        ...updates,
        updatedAt: serverTimestamp(),
      })
      
      console.log('Firebase updateDoc completed successfully')
      
      // Update local state
      set(state => ({
        projects: state.projects.map(p => 
          p.id === id ? { ...p, ...updates } : p
        ),
        currentProject: state.currentProject?.id === id 
          ? { ...state.currentProject, ...updates }
          : state.currentProject
      }))
      
      console.log('Local state updated')
    } catch (error) {
      console.error('updateProject error:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to update project' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  deleteProject: async (id) => {
    try {
      set({ loading: true, error: null })
      await deleteDoc(doc(db, 'projects', id))
      
      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete project' })
    } finally {
      set({ loading: false })
    }
  },

  setCurrentProject: (project) => {
    set({ currentProject: project })
  },

  fetchScenes: async (projectId) => {
    try {
      set({ loading: true, error: null })
      const q = query(
        collection(db, 'scenes'),
        where('projectId', '==', projectId),
        orderBy('orderIndex', 'asc')
      )
      const snapshot = await getDocs(q)
      const scenes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate().toISOString(),
      })) as Scene[]
      
      set({ scenes })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch scenes' })
    } finally {
      set({ loading: false })
    }
  },

  createScene: async (sceneData, initialImageId) => {
    try {
      set({ loading: true, error: null })
      const docRef = await addDoc(collection(db, 'scenes'), {
        ...sceneData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      
      // Update project's scene count
      await updateDoc(doc(db, 'projects', sceneData.projectId), {
        totalScenes: increment(1),
        updatedAt: serverTimestamp(),
      })

      // If initialImageId is provided, move that image to the new scene
      if (initialImageId) {
        const { moveImageToScene } = get()
        await moveImageToScene(initialImageId, docRef.id)
      }
      
      // Refresh scenes
      const { fetchScenes } = get()
      await fetchScenes(sceneData.projectId)
      
      return docRef.id
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create scene' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  updateScene: async (id, updates) => {
    try {
      set({ loading: true, error: null })
      await updateDoc(doc(db, 'scenes', id), {
        ...updates,
        updatedAt: serverTimestamp(),
      })
      
      set(state => ({
        scenes: state.scenes.map(s => 
          s.id === id ? { ...s, ...updates } : s
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update scene' })
    } finally {
      set({ loading: false })
    }
  },

  deleteScene: async (id) => {
    try {
      set({ loading: true, error: null })
      await deleteDoc(doc(db, 'scenes', id))
      
      set(state => ({
        scenes: state.scenes.filter(s => s.id !== id)
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete scene' })
    } finally {
      set({ loading: false })
    }
  },

  reorderScenes: async (projectId, sceneIds) => {
    try {
      set({ loading: true, error: null })
      
      // Update order index for each scene
      const updates = sceneIds.map((sceneId, index) => 
        updateDoc(doc(db, 'scenes', sceneId), { orderIndex: index })
      )
      
      await Promise.all(updates)
      
      // Refresh scenes
      const { fetchScenes } = get()
      await fetchScenes(projectId)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to reorder scenes' })
    } finally {
      set({ loading: false })
    }
  },

  fetchImages: async (sceneId, forceRefresh = false) => {
    try {
      const state = get()
      const now = Date.now()
      
      // Check if we have a valid cache entry and we're not forcing refresh
      const cacheEntry = state.imageCache.get(sceneId)
      if (!forceRefresh && cacheEntry && (now - cacheEntry.fetchedAt) < IMAGE_CACHE_TTL) {
        // Use cached images - just update current images and sceneId
        console.log(`Using cached images for scene ${sceneId}`)
        set({ 
          images: cacheEntry.images,
          currentSceneId: sceneId 
        })
        return
      }
      
      set({ loading: true, error: null, currentSceneId: sceneId })
      console.log(`Fetching images for scene ${sceneId}`)
      
      const q = query(
        collection(db, 'images'),
        where('sceneId', '==', sceneId),
        orderBy('uploadedAt', 'desc')
      )
      const snapshot = await getDocs(q)
      const images = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate().toISOString(),
        comments: doc.data().comments || [],
      })) as Image[]
      
      // Update cache with new images
      const newCache = new Map(state.imageCache)
      newCache.set(sceneId, {
        images,
        fetchedAt: now
      })
      
      set({ 
        images,
        imageCache: newCache
      })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch images' })
    } finally {
      set({ loading: false })
    }
  },

  invalidateImageCache: (sceneId: string) => {
    const state = get()
    const newCache = new Map(state.imageCache)
    newCache.delete(sceneId)
    set({ imageCache: newCache })
  },

  clearImageCache: () => {
    set({ imageCache: new Map() })
  },

  uploadImage: async (file, sceneId, projectId) => {
    try {
      set({ loading: true, error: null })
      
      console.log('Starting upload:', { file: file.name, sceneId, projectId })
      
      const timestamp = Date.now()
      
      // Create storage reference for full image
      const storageRef = ref(storage, `projects/${projectId}/scenes/${sceneId}/${timestamp}_${file.name}`)
      
      console.log('Storage reference created:', storageRef.fullPath)
      
      // Upload full-size file
      const snapshot = await uploadBytes(storageRef, file)
      console.log('File uploaded to storage:', snapshot.ref.fullPath)
      
      const url = await getDownloadURL(snapshot.ref)
      console.log('Download URL obtained:', url)
      
      // Generate and upload thumbnail
      let thumbnailUrl: string | undefined
      try {
        console.log('Generating thumbnail...')
        const thumbnailBlob = await generateThumbnail(file, {
          maxWidth: 400,
          maxHeight: 400,
          quality: 0.8,
          format: 'image/jpeg'
        })
        
        const thumbnailFilename = getThumbnailFilename(file.name)
        const thumbnailRef = ref(storage, `projects/${projectId}/scenes/${sceneId}/thumbnails/${timestamp}_${thumbnailFilename}`)
        
        const thumbnailSnapshot = await uploadBytes(thumbnailRef, thumbnailBlob, {
          contentType: 'image/jpeg'
        })
        
        thumbnailUrl = await getDownloadURL(thumbnailSnapshot.ref)
        console.log('Thumbnail uploaded:', thumbnailUrl)
      } catch (thumbError) {
        console.warn('Thumbnail generation failed, will use full image:', thumbError)
        // Continue without thumbnail - fallback to full image
      }
      
      // Create image document
      const imageData = {
        sceneId,
        projectId,
        url,
        thumbnailUrl: thumbnailUrl || url, // Fallback to full URL if thumbnail failed
        name: file.name,
        uploadedAt: serverTimestamp(),
        isMain: false,
        isFavorite: false,
        metadata: {
          filename: file.name,
          size: file.size,
          width: 0, // Will be updated when image loads
          height: 0,
          format: file.type,
        },
        comments: [],
      }
      
      console.log('Creating Firestore document with data:', imageData)
      const docRef = await addDoc(collection(db, 'images'), imageData)
      console.log('Firestore document created:', docRef.id)
      
      // Update project's image count
      await updateDoc(doc(db, 'projects', projectId), {
        totalImages: increment(1),
        updatedAt: serverTimestamp(),
      })
      
      // Invalidate cache and refresh images
      const { fetchImages, invalidateImageCache } = get()
      invalidateImageCache(sceneId)
      await fetchImages(sceneId, true)
      
      return docRef.id
    } catch (error) {
      console.error('Upload error details:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to upload image' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  generateImage: async (imageUrl, prompt, sceneId, projectId) => {
    try {
      set({ loading: true, error: null })
      
      console.log('Starting AI image generation save:', { imageUrl, sceneId, projectId })
      
      // Fetch the image from the URL
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      
      // Create a filename based on prompt
      const timestamp = Date.now()
      const sanitizedPrompt = prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')
      const filename = `ai_${sanitizedPrompt}_${timestamp}.png`
      
      // Create storage reference
      const storageRef = ref(storage, `projects/${projectId}/scenes/${sceneId}/${filename}`)
      
      console.log('Storage reference created:', storageRef.fullPath)
      
      // Upload the blob to Firebase Storage
      const snapshot = await uploadBytes(storageRef, blob, {
        contentType: 'image/png',
        customMetadata: {
          generatedBy: 'fal-ai',
          model: 'seedream-v4',
          prompt: prompt.slice(0, 500), // Limit metadata size
        }
      })
      console.log('Generated image uploaded to storage:', snapshot.ref.fullPath)
      
      const url = await getDownloadURL(snapshot.ref)
      console.log('Download URL obtained:', url)
      
      // Generate and upload thumbnail
      let thumbnailUrl: string | undefined
      try {
        console.log('Generating thumbnail for AI image...')
        const thumbnailBlob = await generateThumbnail(blob, {
          maxWidth: 400,
          maxHeight: 400,
          quality: 0.8,
          format: 'image/jpeg'
        })
        
        const thumbnailFilename = getThumbnailFilename(filename)
        const thumbnailRef = ref(storage, `projects/${projectId}/scenes/${sceneId}/thumbnails/${thumbnailFilename}`)
        
        const thumbnailSnapshot = await uploadBytes(thumbnailRef, thumbnailBlob, {
          contentType: 'image/jpeg'
        })
        
        thumbnailUrl = await getDownloadURL(thumbnailSnapshot.ref)
        console.log('Thumbnail uploaded:', thumbnailUrl)
      } catch (thumbError) {
        console.warn('Thumbnail generation failed, will use full image:', thumbError)
        // Continue without thumbnail - fallback to full image
      }
      
      // Create image document
      const imageData = {
        sceneId,
        projectId,
        url,
        thumbnailUrl: thumbnailUrl || url, // Fallback to full URL if thumbnail failed
        name: filename,
        prompt: prompt, // Save the generation prompt
        uploadedAt: serverTimestamp(),
        isMain: false,
        isFavorite: false,
        metadata: {
          filename,
          size: blob.size,
          width: 0, // Will be updated when image loads
          height: 0,
          format: 'image/png',
        },
        comments: [],
      }
      
      console.log('Creating Firestore document with data:', imageData)
      const docRef = await addDoc(collection(db, 'images'), imageData)
      console.log('Firestore document created:', docRef.id)
      
      // Update project's image count
      await updateDoc(doc(db, 'projects', projectId), {
        totalImages: increment(1),
        updatedAt: serverTimestamp(),
      })
      
      // Invalidate cache and refresh images
      const { fetchImages, invalidateImageCache } = get()
      invalidateImageCache(sceneId)
      await fetchImages(sceneId, true)
      
      return docRef.id
    } catch (error) {
      console.error('Generate image save error details:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to save generated image' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  updateImage: async (id, updates) => {
    try {
      set({ loading: true, error: null })
      await updateDoc(doc(db, 'images', id), updates)
      
      set(state => ({
        images: state.images.map(img => 
          img.id === id ? { ...img, ...updates } : img
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update image' })
    } finally {
      set({ loading: false })
    }
  },

  moveImageToScene: async (imageId, targetSceneId) => {
    try {
      set({ loading: true, error: null })
      
      // Get the image to check if it was main
      const image = get().images.find(img => img.id === imageId)
      const currentSceneId = image?.sceneId

      // Check if target scene has any images (to determine if this is the first image)
      const targetSceneImagesQuery = query(
        collection(db, 'images'),
        where('sceneId', '==', targetSceneId)
      )
      const targetSceneImagesSnapshot = await getDocs(targetSceneImagesQuery)
      const isFirstImageInScene = targetSceneImagesSnapshot.empty
      
      // Update the image with new sceneId
      // If it's the first image in the scene, automatically set it as main
      await updateDoc(doc(db, 'images', imageId), {
        sceneId: targetSceneId,
        isMain: isFirstImageInScene, // Auto-star if first image
        updatedAt: serverTimestamp()
      })
      
      // If this is the first image in the scene, also update the scene's mainImageId and thumbnail
      if (isFirstImageInScene && image) {
        const sceneThumbnailUrl = image.thumbnailUrl || image.url
        await updateDoc(doc(db, 'scenes', targetSceneId), {
          mainImageId: imageId,
          thumbnailUrl: sceneThumbnailUrl
        })
        
        // Update local scenes state immediately for instant thumbnail update
        set(state => ({
          scenes: state.scenes.map(s => 
            s.id === targetSceneId 
              ? { ...s, mainImageId: imageId, thumbnailUrl: sceneThumbnailUrl } 
              : s
          )
        }))
      }
      
      // If the image was main in the old scene, clear mainImageId of old scene
      if (currentSceneId && image?.isMain) {
         await updateDoc(doc(db, 'scenes', currentSceneId), {
           mainImageId: null,
           thumbnailUrl: null
         })
         
         // Update local scenes state to reflect this change
         set(state => ({
            scenes: state.scenes.map(s => 
              s.id === currentSceneId ? { ...s, mainImageId: undefined, thumbnailUrl: undefined } : s
            )
         }))
      }

      // Invalidate cache for both source and target scenes
      const { invalidateImageCache } = get()
      if (currentSceneId) {
        invalidateImageCache(currentSceneId)
      }
      invalidateImageCache(targetSceneId)

      // Update local state - remove from current list if we are viewing the source scene
      set(state => ({
        images: state.images.filter(img => img.id !== imageId)
      }))
      
      // Note: We don't need to add it to the new scene in local state immediately 
      // because we are likely still viewing the old scene.
      // If we were viewing "all images" (which we don't support yet per scene view), 
      // we would update the sceneId in the object.

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to move image' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  deleteImage: async (id) => {
    try {
      set({ loading: true, error: null })
      
      // Get image data to delete from storage
      const imageDoc = await getDoc(doc(db, 'images', id))
      if (imageDoc.exists()) {
        const imageData = imageDoc.data() as Image
        const sceneId = imageData.sceneId
        
        // Delete full image from storage
        try {
          const storageRef = ref(storage, imageData.url)
          await deleteObject(storageRef)
        } catch (e) {
          console.warn('Failed to delete full image from storage:', e)
        }
        
        // Delete thumbnail from storage if it exists and is different from full URL
        if (imageData.thumbnailUrl && imageData.thumbnailUrl !== imageData.url) {
          try {
            const thumbRef = ref(storage, imageData.thumbnailUrl)
            await deleteObject(thumbRef)
          } catch (e) {
            console.warn('Failed to delete thumbnail from storage:', e)
          }
        }
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'images', id))
        
        // Invalidate cache for this scene
        const { invalidateImageCache } = get()
        invalidateImageCache(sceneId)
        
        set(state => ({
          images: state.images.filter(img => img.id !== id)
        }))
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete image' })
    } finally {
      set({ loading: false })
    }
  },

  setMainImage: async (imageId, sceneId) => {
    try {
      set({ loading: true, error: null })
      
      // Get the image URL for the scene thumbnail (prefer thumbnail URL)
      const selectedImage = get().images.find(img => img.id === imageId)
      
      // First, unset all main images in the scene
      const images = get().images.filter(img => img.sceneId === sceneId)
      await Promise.all(
        images.map(img => 
          updateDoc(doc(db, 'images', img.id), { isMain: false })
        )
      )
      
      // Set the selected image as main
      await updateDoc(doc(db, 'images', imageId), { isMain: true })
      
      // Update scene's main image and thumbnail (use thumbnail URL if available)
      const sceneThumbnailUrl = selectedImage?.thumbnailUrl || selectedImage?.url
      await updateDoc(doc(db, 'scenes', sceneId), { 
        mainImageId: imageId,
        ...(sceneThumbnailUrl && { thumbnailUrl: sceneThumbnailUrl })
      })
      
      // Update local scene state
      set(state => ({
        scenes: state.scenes.map(scene =>
          scene.id === sceneId
            ? { ...scene, mainImageId: imageId, thumbnailUrl: sceneThumbnailUrl }
            : scene
        )
      }))
      
      // Invalidate cache and refresh images
      const { fetchImages, invalidateImageCache } = get()
      invalidateImageCache(sceneId)
      await fetchImages(sceneId, true)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to set main image' })
    } finally {
      set({ loading: false })
    }
  },

  toggleFavorite: async (imageId) => {
    try {
      const image = get().images.find(img => img.id === imageId)
      if (!image) return
      
      await updateDoc(doc(db, 'images', imageId), { 
        isFavorite: !image.isFavorite 
      })
      
      set(state => ({
        images: state.images.map(img => 
          img.id === imageId ? { ...img, isFavorite: !img.isFavorite } : img
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to toggle favorite' })
    }
  },

  addComment: async (imageId, content, userId, userEmail) => {
    try {
      const comment: Comment = {
        id: Date.now().toString(),
        imageId,
        userId,
        userEmail,
        content,
        createdAt: new Date().toISOString(),
        replies: [],
      }
      
      await updateDoc(doc(db, 'images', imageId), {
        comments: arrayUnion(comment)
      })
      
      set(state => ({
        images: state.images.map(img => 
          img.id === imageId 
            ? { ...img, comments: [...img.comments, comment] }
            : img
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add comment' })
    }
  },

  addReply: async (commentId, content, userId, userEmail) => {
    try {
      const reply: Reply = {
        id: Date.now().toString(),
        commentId,
        userId,
        userEmail,
        content,
        createdAt: new Date().toISOString(),
      }
      
      // Find the image and comment to update
      const image = get().images.find(img => 
        img.comments.some(comment => comment.id === commentId)
      )
      
      if (image) {
        const updatedComments = image.comments.map(comment => 
          comment.id === commentId 
            ? { ...comment, replies: [...comment.replies, reply] }
            : comment
        )
        
        await updateDoc(doc(db, 'images', image.id), {
          comments: updatedComments
        })
        
        set(state => ({
          images: state.images.map(img => 
            img.id === image.id 
              ? { ...img, comments: updatedComments }
              : img
          )
        }))
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add reply' })
    }
  },

  deleteComment: async (commentId, imageId) => {
    try {
      const image = get().images.find(img => img.id === imageId)
      if (!image) return
      
      const updatedComments = image.comments.filter(comment => comment.id !== commentId)
      
      await updateDoc(doc(db, 'images', imageId), {
        comments: updatedComments
      })
      
      set(state => ({
        images: state.images.map(img => 
          img.id === imageId 
            ? { ...img, comments: updatedComments }
            : img
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete comment' })
    }
  },

  updateProjectSettings: async (projectId, settings) => {
    try {
      set({ loading: true, error: null })
      await updateDoc(doc(db, 'projects', projectId), {
        title: settings.name,
        description: settings.description,
        aspectRatio: settings.aspectRatio,
        customAspectRatio: settings.customAspectRatio,
        sharedWith: settings.sharedUsers,
        isPublic: settings.isPublic,
        allowComments: settings.allowComments,
        updatedAt: serverTimestamp(),
      })
      
      // Update local state
      set(state => ({
        projects: state.projects.map(p => 
          p.id === projectId ? { 
            ...p, 
            title: settings.name,
            description: settings.description,
            aspectRatio: settings.aspectRatio,
            sharedWith: settings.sharedUsers,
            isPublic: settings.isPublic,
            allowComments: settings.allowComments,
          } : p
        ),
        currentProject: state.currentProject?.id === projectId 
          ? { 
              ...state.currentProject, 
              title: settings.name,
              description: settings.description,
              aspectRatio: settings.aspectRatio,
              sharedWith: settings.sharedUsers,
              isPublic: settings.isPublic,
              allowComments: settings.allowComments,
            }
          : state.currentProject
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update project settings' })
    } finally {
      set({ loading: false })
    }
  },

  regenerateThumbnails: async (projectId, sceneId) => {
    try {
      set({ loading: true, error: null })
      
      const regenerateFn = httpsCallable<
        { projectId: string; sceneId?: string },
        { success: boolean; processed?: { success: number; failed: number; skipped: number }; error?: string }
      >(functions, 'regenerateThumbnails')
      
      const result = await regenerateFn({ projectId, sceneId })
      
      if (result.data.success) {
        // Invalidate cache for the affected scene(s)
        const { invalidateImageCache, clearImageCache, fetchImages, currentSceneId } = get()
        
        if (sceneId) {
          invalidateImageCache(sceneId)
          // Refresh current scene if it's the one we regenerated
          if (currentSceneId === sceneId) {
            await fetchImages(sceneId, true)
          }
        } else {
          // Clear all cache if we regenerated all scenes
          clearImageCache()
          if (currentSceneId) {
            await fetchImages(currentSceneId, true)
          }
        }
      }
      
      return result.data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to regenerate thumbnails'
      set({ error: errorMessage })
      return { success: false, error: errorMessage }
    } finally {
      set({ loading: false })
    }
  },

  // Module actions
  setCurrentModule: (module: ModuleType) => {
    set({ currentModule: module })
  },

  // Video actions
  fetchVideos: async (sceneId, forceRefresh = false) => {
    try {
      const state = get()
      const now = Date.now()
      
      // Check if we have a valid cache entry and we're not forcing refresh
      const cacheEntry = state.videoCache.get(sceneId)
      if (!forceRefresh && cacheEntry && (now - cacheEntry.fetchedAt) < VIDEO_CACHE_TTL) {
        // Use cached videos
        console.log(`Using cached videos for scene ${sceneId}`)
        set({ videos: cacheEntry.videos })
        return
      }
      
      set({ loading: true, error: null })
      console.log(`Fetching videos for scene ${sceneId}`)
      
      const q = query(
        collection(db, 'videos'),
        where('sceneId', '==', sceneId),
        orderBy('uploadedAt', 'desc')
      )
      const snapshot = await getDocs(q)
      const videos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        uploadedAt: doc.data().uploadedAt?.toDate().toISOString(),
        comments: doc.data().comments || [],
      })) as Video[]
      
      // Update cache with new videos
      const newCache = new Map(state.videoCache)
      newCache.set(sceneId, {
        videos,
        fetchedAt: now
      })
      
      set({ 
        videos,
        videoCache: newCache
      })
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to fetch videos' })
    } finally {
      set({ loading: false })
    }
  },

  uploadVideo: async (file, sceneId, projectId) => {
    try {
      set({ loading: true, error: null })
      
      console.log('Starting video upload:', { file: file.name, sceneId, projectId })
      
      const timestamp = Date.now()
      
      // Create storage reference for video
      const storageRef = ref(storage, `projects/${projectId}/scenes/${sceneId}/videos/${timestamp}_${file.name}`)
      
      console.log('Storage reference created:', storageRef.fullPath)
      
      // Upload video file
      const snapshot = await uploadBytes(storageRef, file)
      console.log('Video uploaded to storage:', snapshot.ref.fullPath)
      
      const url = await getDownloadURL(snapshot.ref)
      console.log('Download URL obtained:', url)
      
      // Create video document
      const videoData = {
        sceneId,
        projectId,
        url,
        thumbnailUrl: '', // Will be generated later or extracted from video
        name: file.name,
        uploadedAt: serverTimestamp(),
        isMain: false,
        isFavorite: false,
        metadata: {
          filename: file.name,
          size: file.size,
          width: 0,
          height: 0,
          duration: 0,
          format: file.type,
        },
        comments: [],
      }
      
      console.log('Creating Firestore document with data:', videoData)
      const docRef = await addDoc(collection(db, 'videos'), videoData)
      console.log('Firestore document created:', docRef.id)
      
      // Invalidate cache and refresh videos
      const { fetchVideos, invalidateVideoCache } = get()
      invalidateVideoCache(sceneId)
      await fetchVideos(sceneId, true)
      
      return docRef.id
    } catch (error) {
      console.error('Video upload error:', error)
      set({ error: error instanceof Error ? error.message : 'Failed to upload video' })
      throw error
    } finally {
      set({ loading: false })
    }
  },

  updateVideo: async (id, updates) => {
    try {
      set({ loading: true, error: null })
      await updateDoc(doc(db, 'videos', id), updates)
      
      set(state => ({
        videos: state.videos.map(vid => 
          vid.id === id ? { ...vid, ...updates } : vid
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update video' })
    } finally {
      set({ loading: false })
    }
  },

  deleteVideo: async (id) => {
    try {
      set({ loading: true, error: null })
      
      // Get video data to delete from storage
      const videoDoc = await getDoc(doc(db, 'videos', id))
      if (videoDoc.exists()) {
        const videoData = videoDoc.data() as Video
        const sceneId = videoData.sceneId
        
        // Delete video from storage
        try {
          const storageRef = ref(storage, videoData.url)
          await deleteObject(storageRef)
        } catch (e) {
          console.warn('Failed to delete video from storage:', e)
        }
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'videos', id))
        
        // Invalidate cache for this scene
        const { invalidateVideoCache } = get()
        invalidateVideoCache(sceneId)
        
        set(state => ({
          videos: state.videos.filter(vid => vid.id !== id)
        }))
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete video' })
    } finally {
      set({ loading: false })
    }
  },

  setMainVideo: async (videoId, sceneId) => {
    try {
      set({ loading: true, error: null })
      
      // First, unset all main videos in the scene
      const videos = get().videos.filter(vid => vid.sceneId === sceneId)
      await Promise.all(
        videos.map(vid => 
          updateDoc(doc(db, 'videos', vid.id), { isMain: false })
        )
      )
      
      // Set the selected video as main
      await updateDoc(doc(db, 'videos', videoId), { isMain: true })
      
      // Update scene's main video
      await updateDoc(doc(db, 'scenes', sceneId), { 
        mainVideoId: videoId
      })
      
      // Update local scene state
      set(state => ({
        scenes: state.scenes.map(scene =>
          scene.id === sceneId
            ? { ...scene, mainVideoId: videoId }
            : scene
        )
      }))
      
      // Invalidate cache and refresh videos
      const { fetchVideos, invalidateVideoCache } = get()
      invalidateVideoCache(sceneId)
      await fetchVideos(sceneId, true)
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to set main video' })
    } finally {
      set({ loading: false })
    }
  },

  toggleVideoFavorite: async (videoId) => {
    try {
      const video = get().videos.find(vid => vid.id === videoId)
      if (!video) return
      
      await updateDoc(doc(db, 'videos', videoId), { 
        isFavorite: !video.isFavorite 
      })
      
      set(state => ({
        videos: state.videos.map(vid => 
          vid.id === videoId ? { ...vid, isFavorite: !vid.isFavorite } : vid
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to toggle favorite' })
    }
  },

  invalidateVideoCache: (sceneId: string) => {
    const state = get()
    const newCache = new Map(state.videoCache)
    newCache.delete(sceneId)
    set({ videoCache: newCache })
  },

  clearVideoCache: () => {
    set({ videoCache: new Map() })
  },

  addVideoComment: async (videoId, content, userId, userEmail) => {
    try {
      const comment: Comment = {
        id: Date.now().toString(),
        imageId: videoId, // Reusing Comment type, imageId here represents the video
        userId,
        userEmail,
        content,
        createdAt: new Date().toISOString(),
        replies: [],
      }
      
      await updateDoc(doc(db, 'videos', videoId), {
        comments: arrayUnion(comment)
      })
      
      set(state => ({
        videos: state.videos.map(vid => 
          vid.id === videoId 
            ? { ...vid, comments: [...vid.comments, comment] }
            : vid
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add comment' })
    }
  },

  // Story content actions
  updateStoryContent: async (sceneId, storyContent) => {
    try {
      set({ loading: true, error: null })
      
      // Get current scene's story content
      const scene = get().scenes.find(s => s.id === sceneId)
      const currentStory = scene?.storyContent || {
        sceneDescription: '',
        soundFx: '',
        music: '',
        dialogue: '',
        voiceOver: '',
        location: '',
        timeOfDay: '',
        characters: [],
        cameraNotes: '',
        duration: '',
        notes: ''
      }
      
      // Merge with updates
      const updatedStory = { ...currentStory, ...storyContent }
      
      await updateDoc(doc(db, 'scenes', sceneId), {
        storyContent: updatedStory,
        updatedAt: serverTimestamp()
      })
      
      set(state => ({
        scenes: state.scenes.map(s => 
          s.id === sceneId ? { ...s, storyContent: updatedStory } : s
        )
      }))
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update story content' })
    } finally {
      set({ loading: false })
    }
  },

  clearError: () => set({ error: null }),
}))

// Queue processing function
const processQueue = async (get: () => ProjectState, set: (state: Partial<ProjectState> | ((state: ProjectState) => Partial<ProjectState>)) => void) => {
  set({ isProcessingQueue: true })

  try {
    while (true) {
      const state = get()
      const nextRequest = state.generationQueue.find(req => req.status === 'pending')
      
      if (!nextRequest) {
        break // No more pending requests
      }

      // Mark as processing
      set(state => ({
        generationQueue: state.generationQueue.map(req => 
          req.id === nextRequest.id 
            ? { ...req, status: 'processing', stage: 'Initializing AI...', progress: 10 } 
            : req
        )
      }))

      try {
        // Execute generation
        const { mode, prompt, settings } = nextRequest
        
        let modelId = ''
        let apiInput: any = {
          prompt: prompt.trim(),
          num_images: settings.numImages || 1,
          enable_safety_checker: true,
          seed: settings.seed
        }

        // Configure input based on mode
        switch (mode) {
          case 'seedream':
            modelId = 'fal-ai/bytedance/seedream/v4.5/text-to-image'
            // Parse resolution string "1024x1024"
            const [width, height] = (settings.resolution || '1024x1024').split('x').map(Number)
            apiInput.image_size = { width, height }
            break
            
          case 'seedream-edit':
            modelId = 'fal-ai/bytedance/seedream/v4.5/edit'
            const [w, h] = (settings.resolution || '1024x1024').split('x').map(Number)
            apiInput.image_size = { width: w, height: h }
            if (settings.imageUrls && settings.imageUrls.length > 0) {
              apiInput.image_urls = settings.imageUrls
            }
            break
            
          case 'nano-banana':
            modelId = 'fal-ai/nano-banana'
            apiInput.aspect_ratio = settings.aspectRatio
            break
            
          case 'nano-banana-edit':
            modelId = 'fal-ai/nano-banana/edit'
            apiInput.aspect_ratio = settings.aspectRatio
            if (settings.imageUrls && settings.imageUrls.length > 0) {
              apiInput.image_url = settings.imageUrls[0]
            }
            break
            
          case 'nano-banana-pro':
            // HD text-to-image generation
            modelId = 'fal-ai/nano-banana-pro'
            apiInput.aspect_ratio = settings.aspectRatio
            // Use HD resolution dimensions
            if (settings.hdWidth && settings.hdHeight) {
              apiInput.image_size = { width: settings.hdWidth, height: settings.hdHeight }
            }
            console.log('HD Generation (nano-banana-pro):', {
              aspectRatio: settings.aspectRatio,
              hdResolution: settings.hdResolution,
              dimensions: { width: settings.hdWidth, height: settings.hdHeight }
            })
            break
            
          case 'nano-banana-pro-edit':
            // HD image-to-image generation
            modelId = 'fal-ai/nano-banana-pro/edit'
            apiInput.aspect_ratio = settings.aspectRatio
            // Use HD resolution dimensions
            if (settings.hdWidth && settings.hdHeight) {
              apiInput.image_size = { width: settings.hdWidth, height: settings.hdHeight }
            }
            if (settings.imageUrls && settings.imageUrls.length > 0) {
              apiInput.image_url = settings.imageUrls[0]
            }
            console.log('HD Edit Generation (nano-banana-pro/edit):', {
              aspectRatio: settings.aspectRatio,
              hdResolution: settings.hdResolution,
              dimensions: { width: settings.hdWidth, height: settings.hdHeight },
              imageUrl: settings.imageUrls?.[0]
            })
            break
            
          case 'flux-pro':
            modelId = 'fal-ai/flux-2-pro'
            // Map settings to Flux API
            if (settings.imageSize === 'custom' && settings.resolution) {
              const [cw, ch] = settings.resolution.split('x').map(Number)
              apiInput.image_size = { width: cw, height: ch }
            } else {
              apiInput.image_size = settings.imageSize || 'landscape_4_3'
            }
            
            if (settings.safetyTolerance) {
              apiInput.safety_tolerance = settings.safetyTolerance
            }
            
            if (settings.enableSafetyChecker !== undefined) {
              apiInput.enable_safety_checker = settings.enableSafetyChecker
            }
            
            if (settings.outputFormat) {
              apiInput.output_format = settings.outputFormat
            }
            
            // Flux Pro doesn't support num_images > 1 natively in a single request usually return 1 image
            // We'll handle this by looping if needed, but for now we assume 1 image per request or handle array output if model supports it
            // Based on docs: "images": [{"url":...}]
            // The docs don't explicitly mention num_images for Flux Pro, it implies 1. 
            // If the user requested multiple, we'd ideally loop here or spawn multiple requests. 
            // For simplicity, let's just make one call. The queue UI allows adding multiple.
            break
            
          case 'character':
            // Use flux-lora for character-based generation with trained LoRA weights
            modelId = 'fal-ai/flux-lora'
            
            // Image size settings
            if (settings.imageSize === 'custom' && settings.resolution) {
              const [cw, ch] = settings.resolution.split('x').map(Number)
              apiInput.image_size = { width: cw, height: ch }
            } else {
              apiInput.image_size = settings.imageSize || 'landscape_4_3'
            }
            
            // LoRA weights configuration - this is the key for character generation
            if (settings.loraWeightsUrl) {
              apiInput.loras = [
                {
                  path: settings.loraWeightsUrl,
                  scale: settings.loraScale || 1.0
                }
              ]
            }
            
            if (settings.outputFormat) {
              apiInput.output_format = settings.outputFormat
            }
            
            // Safety settings
            if (settings.enableSafetyChecker !== undefined) {
              apiInput.enable_safety_checker = settings.enableSafetyChecker
            }
            
            console.log('Character generation with LoRA:', {
              characterName: settings.characterName,
              loraWeightsUrl: settings.loraWeightsUrl,
              loraScale: settings.loraScale
            })
            break
        }

        console.log(`Processing queue item ${nextRequest.id}: Submitting to ${modelId}`)
        
        // Update stage
        set(state => ({
          generationQueue: state.generationQueue.map(req => 
            req.id === nextRequest.id 
              ? { ...req, stage: 'Generating...', progress: 30 } 
              : req
          )
        }))

        // Call fal.ai
        const result: any = await fal.subscribe(modelId, {
          input: apiInput,
          logs: true,
          onQueueUpdate: (update) => {
            if (update.status === 'IN_PROGRESS') {
              set(state => ({
                generationQueue: state.generationQueue.map(req => 
                  req.id === nextRequest.id 
                    ? { ...req, stage: 'AI Processing...', progress: 50 + (update.logs ? 10 : 0) } 
                    : req
                )
              }))
            }
          },
        })

        // Extract images
        const images = result.images || result.data?.images || []
        if (images.length === 0) throw new Error('No images generated')

        set(state => ({
          generationQueue: state.generationQueue.map(req => 
            req.id === nextRequest.id 
              ? { ...req, stage: 'Saving to Gallery...', progress: 80 } 
              : req
          )
        }))

        // Save each image
        for (let i = 0; i < images.length; i++) {
          const img = images[i]
          await state.generateImage(img.url, prompt, nextRequest.sceneId, nextRequest.projectId)
          
          set(state => ({
            generationQueue: state.generationQueue.map(req => 
              req.id === nextRequest.id 
                ? { ...req, progress: 80 + ((i + 1) / images.length * 20) } 
                : req
            )
          }))
        }

        // Mark as completed
        set(state => ({
          generationQueue: state.generationQueue.map(req => 
            req.id === nextRequest.id 
              ? { ...req, status: 'completed', stage: 'Complete', progress: 100 } 
              : req
          )
        }))

      } catch (error) {
        console.error(`Queue processing failed for ${nextRequest.id}:`, error)
        set(state => ({
          generationQueue: state.generationQueue.map(req => 
            req.id === nextRequest.id 
              ? { 
                  ...req, 
                  status: 'failed', 
                  error: error instanceof Error ? error.message : 'Unknown error',
                  stage: 'Failed'
                } 
              : req
          )
        }))
      }
    }
  } catch (err) {
    console.error('Fatal queue processing error:', err)
  } finally {
    set({ isProcessingQueue: false })
  }
}
